import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DataTable } from '../ui/data-table';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import FileUpload from '../ui/FileUpload';
import { classSharedFileAPI, userAPI, cacheUtils } from '../../services/api';
import s3Service from '../../services/s3Service';

const ClassSharedFiles = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const classTitle = location.state?.classTitle || 'Class';
  
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isShareFolderModalOpen, setIsShareFolderModalOpen] = useState(false);
  const [s3Folders, setS3Folders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedS3Folder, setSelectedS3Folder] = useState('');
  const [folderFiles, setFolderFiles] = useState([]);
  const [loadingFolderFiles, setLoadingFolderFiles] = useState(false);
  const [sharingFolder, setSharingFolder] = useState(false);
  const [currentS3Path, setCurrentS3Path] = useState('');
  const [pathHistory, setPathHistory] = useState([]);
  const [editingFileId, setEditingFileId] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [formData, setFormData] = useState({
    file_name: '',
    file_url: '',
    file_type: '',
    file_size: 0,
    description: '',
  });
  
  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      sortable: true
    },
    {
      accessorKey: 'file_name',
      header: 'File Name',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center space-x-2">
          {getFileIcon(row.file_type)}
          <span className="font-medium">{row.file_name}</span>
        </div>
      )
    },
    {
      accessorKey: 'file_type',
      header: 'Type',
      sortable: true,
      cell: (row) => (
        <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
          {row.file_type?.toUpperCase() || 'FILE'}
        </span>
      )
    },
    {
      accessorKey: 'file_size',
      header: 'Size',
      sortable: true,
      cell: (row) => formatFileSize(row.file_size)
    },
    {
      accessorKey: 'description',
      header: 'Description',
      sortable: false,
      cell: (row) => (
        <span className="text-gray-600 truncate max-w-xs block">
          {row.description || 'No description'}
        </span>
      )
    },
    {
      accessorKey: 'uploaded_at',
      header: 'Uploaded',
      sortable: true,
      cell: (row) => new Date(row.created_at).toLocaleDateString()
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      sortable: false,
      className: 'w-40',
      cell: (row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleViewFile(row)}
            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="View file details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => window.open(row.file_url, '_blank')}
            className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
            title="Download file"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
          <button
            onClick={() => handleShareFile(row)}
            className="p-1.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-full transition-colors"
            title="Share file"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </button>
          <button
            onClick={() => handleEditFile(row)}
            className="p-1.5 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors"
            title="Edit file"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteFile(row.id)}
            disabled={deletingFileId === row.id}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete file"
          >
            {deletingFileId === row.id ? (
              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchFiles();
    fetchCurrentUser();
  }, [classId]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await classSharedFileAPI.getClassSharedFilesByClassId(classId);
      setFiles(response.data || []);
    } catch (error) {
      console.error('Error fetching shared files:', error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      // Get current user from localStorage or session
      const userStr = localStorage.getItem('adminUser') || sessionStorage.getItem('adminUser');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        console.log('Current user:', currentUser);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };


  const handleFileUploadSuccess = (url, fileInfo) => {
    setFormData(prev => ({
      ...prev,
      file_url: url,
      file_name: prev.file_name || fileInfo.fileName || 'Uploaded File',
      file_type: fileInfo.fileType || prev.file_type || '',
      file_size: fileInfo.fileSize || prev.file_size || 0
    }));
  };

  const handleFileUploadError = (error) => {
    console.error('File upload error:', error);
    alert('Failed to upload file. Please try again.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.file_url) {
      alert('Please upload a file first');
      return;
    }

    try {
      setIsSubmitting(true);
      const submitData = {
        class_id: parseInt(classId),
        file_name: formData.file_name,
        file_url: formData.file_url,
        file_type: formData.file_type,
        file_size: parseInt(formData.file_size),
        description: formData.description,
        uploaded_by:  1, // Use current user ID
      };

      if (isEditing) {
        await classSharedFileAPI.updateClassSharedFile(editingFileId, submitData);
        // Clear cache after update
        cacheUtils.clearClassSharedFileCache();
      } else {
        await classSharedFileAPI.createClassSharedFile(submitData);
        // Clear cache after create
        cacheUtils.clearClassSharedFileCache();
      }

      setIsModalOpen(false);
      resetForm();
      fetchFiles();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} shared file:`, error);
      alert(`Failed to ${isEditing ? 'update' : 'create'} shared file. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditFile = (file) => {
    setIsModalOpen(true);
    setIsEditing(true);
    setEditingFileId(file.id);
    setFormData({
      file_name: file.file_name || '',
      file_url: file.file_url || '',
      file_type: file.file_type || '',
      file_size: file.file_size || 0,
      description: file.description || '',
    });
    setIsModalOpen(true);
  };

  const handleViewFile = (file) => {
    setSelectedFile(file);
    setIsViewModalOpen(true);
  };

  const handleShareFile = async (file) => {
    try {
      setGeneratingLink(true);
      setLinkCopied(false);
      setSelectedFile(file);
      
      // Extract S3 key from file URL
      const url = new URL(file.file_url);
      const objectKey = decodeURIComponent(url.pathname.substring(1)); // Remove leading '/'
      
      // Generate presigned URL (7 days expiration)
      const result = await s3Service.generateShareableLink(objectKey, 604800);
      
      setShareableLink(result.url);
      setIsShareModalOpen(true);
    } catch (error) {
      console.error('Error generating shareable link:', error);
      alert('Failed to generate shareable link. Please try again.');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareableLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
        alert('Failed to copy link to clipboard');
      });
  };

  const handleOpenShareFolderModal = async () => {
    setIsShareFolderModalOpen(true);
    setCurrentS3Path('');
    setPathHistory([]);
    setSelectedS3Folder('');
    setFolderFiles([]);
    await loadFoldersAndFiles('');
  };

  const loadFoldersAndFiles = async (path) => {
    setLoadingFolders(true);
    try {
      const objects = await s3Service.listObjects(path);
      // Separate folders and files
      const folders = objects.filter(obj => obj.isFolder);
      const files = objects.filter(obj => !obj.isFolder);
      setS3Folders(folders);
      setFolderFiles(files);
    } catch (error) {
      console.error('Error fetching S3 contents:', error);
      alert('Failed to load S3 contents');
      setS3Folders([]);
      setFolderFiles([]);
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleNavigateIntoFolder = async (folderPath) => {
    setPathHistory(prev => [...prev, currentS3Path]);
    setCurrentS3Path(folderPath);
    setSelectedS3Folder('');
    await loadFoldersAndFiles(folderPath);
  };

  const handleNavigateBack = async () => {
    if (pathHistory.length > 0) {
      const previousPath = pathHistory[pathHistory.length - 1];
      setPathHistory(prev => prev.slice(0, -1));
      setCurrentS3Path(previousPath);
      setSelectedS3Folder('');
      await loadFoldersAndFiles(previousPath);
    }
  };

  const handleSelectFolder = (folderPath) => {
    if (selectedS3Folder === folderPath) {
      setSelectedS3Folder('');
    } else {
      setSelectedS3Folder(folderPath);
    }
  };

  const handleShareFolderToClass = async () => {
    // If a specific folder is selected, use it; otherwise use current path
    const folderToShare = selectedS3Folder || currentS3Path;
    
    if (!folderToShare) {
      alert('Please select a folder first');
      return;
    }

    const folderName = folderToShare.replace(/\/$/, '').split('/').pop();
    
    if (!window.confirm(`Share folder "${folderName}" to this class?`)) {
      return;
    }

    try {
      setSharingFolder(true);
      const bucketName = process.env.REACT_APP_S3_BUCKET_NAME;
      const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';

      // Create folder URL
      const folderUrl = `https://${bucketName}.s3.${region}.amazonaws.com/${folderToShare}`;
      
      const submitData = {
        class_id: parseInt(classId),
        file_name: folderName,
        file_url: folderUrl,
        file_type: 'folder',
        file_size: 0,
        description: `S3 Folder containing ${folderFiles.length} file(s)`,
        uploaded_by: 1,
      };

      await classSharedFileAPI.createClassSharedFile(submitData);

      // Clear cache after sharing
      cacheUtils.clearClassSharedFileCache();
      
      // Refresh files list
      await fetchFiles();

      // Close modal and reset state
      setIsShareFolderModalOpen(false);
      setSelectedS3Folder('');
      setFolderFiles([]);

      alert(`Successfully shared folder "${folderName}" to the class!`);
    } catch (error) {
      console.error('Error sharing folder to class:', error);
      alert('Failed to share folder to class. Please try again.');
    } finally {
      setSharingFolder(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingFileId(fileId);
      await classSharedFileAPI.deleteClassSharedFile(fileId);
      // Clear cache after delete
      cacheUtils.clearClassSharedFileCache();
      fetchFiles();
    } catch (error) {
      console.error('Error deleting shared file:', error);
      alert('Failed to delete shared file. Please try again.');
    } finally {
      setDeletingFileId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      file_name: '',
      file_url: '',
      file_type: '',
      file_size: 0,
      description: '',
    });
    setIsEditing(false);
    setEditingFileId(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleGoBack = () => {
    navigate(`/classes/${classId}`);
  };

  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase() || '';
    
    if (type.includes('pdf')) {
      return (
        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5z" />
        </svg>
      );
    } else if (type.includes('doc') || type.includes('word')) {
      return (
        <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5z" />
        </svg>
      );
    } else if (type.includes('image') || type.includes('jpg') || type.includes('png')) {
      return (
        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      );
    } else if (type.includes('video')) {
      return (
        <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
        </svg>
      );
    } else {
      return (
        <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="max-w-6xl mx-auto p-9">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleGoBack}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              title="Back to class details"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Shared Files</h1>
              <p className="text-lg text-gray-600">{classTitle} - Manage shared files</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleOpenShareFolderModal}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              <span>Share Folder</span>
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Upload File</span>
            </button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={files}
        searchKey="file_name"
        searchPlaceholder="Search files..."
        loading={loading}
        emptyMessage="No shared files found."
      />

      {/* Upload/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} disableBackdropClose={true}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Shared File' : 'Upload New File'}
            </h2>
          </ModalHeader>

          <ModalBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                File Upload *
              </label>
              <FileUpload
                onUploadSuccess={handleFileUploadSuccess}
                onUploadError={handleFileUploadError}
                accept="*/*"
                maxSize={50 * 1024 * 1024} // 50MB
                folder="class-shared-files"
                showPreview={false}
                currentUrl={formData.file_url}
              />
              {formData.file_url && (
                <div className="mt-2">
                  <p className="text-xs text-green-600">File uploaded successfully</p>
                  <a 
                    href={formData.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    View uploaded file
                  </a>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="file_name" className="block text-sm font-medium text-gray-700 mb-1">
                File Name *
              </label>
              <Input
                id="file_name"
                name="file_name"
                type="text"
                value={formData.file_name}
                onChange={handleInputChange}
                required
                placeholder="Enter file name"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter file description (optional)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="file_type" className="block text-sm font-medium text-gray-700 mb-1">
                  File Type
                </label>
                <Input
                  id="file_type"
                  name="file_type"
                  type="text"
                  value={formData.file_type}
                  onChange={handleInputChange}
                  placeholder="e.g., pdf, docx"
                />
              </div>

              <div>
                <label htmlFor="file_size" className="block text-sm font-medium text-gray-700 mb-1">
                  File Size (bytes)
                </label>
                <Input
                  id="file_size"
                  name="file_size"
                  type="number"
                  value={formData.file_size}
                  onChange={handleInputChange}
                  placeholder="Auto-detected"
                  disabled
                />
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              onClick={handleCloseModal}
              className="mr-2 bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.file_url}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Uploading...') 
                : (isEditing ? 'Update File' : 'Upload File')
              }
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* View File Details Modal */}
      <Modal 
        isOpen={isViewModalOpen} 
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedFile(null);
        }}
        className="max-w-2xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">File Details</h2>
        </ModalHeader>

        <ModalBody className="space-y-4">
          {selectedFile && (
            <div>
              <div className="flex items-start space-x-4 mb-4">
                <div className="flex-shrink-0">
                  {getFileIcon(selectedFile.file_type)}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {selectedFile.file_name}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="font-medium text-gray-700">File Type:</span>
                      <span className="text-gray-600">{selectedFile.file_type?.toUpperCase() || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="font-medium text-gray-700">File Size:</span>
                      <span className="text-gray-600">{formatFileSize(selectedFile.file_size)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="font-medium text-gray-700">Uploaded:</span>
                      <span className="text-gray-600">
                        {new Date(selectedFile.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="font-medium text-gray-700">File ID:</span>
                      <span className="text-gray-600">{selectedFile.id}</span>
                    </div>
                  </div>
                </div>
              </div>

              {selectedFile.description && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Description:</h4>
                  <p className="text-gray-600">{selectedFile.description}</p>
                </div>
              )}

              <div className="flex space-x-2 mt-4">
                <Button
                  onClick={() => window.open(selectedFile.file_url, '_blank')}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download File
                </Button>
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={() => {
              setIsViewModalOpen(false);
              setSelectedFile(null);
            }}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Share Link Modal */}
      <Modal 
        isOpen={isShareModalOpen} 
        onClose={() => {
          setIsShareModalOpen(false);
          setShareableLink('');
          setLinkCopied(false);
        }}
        className="max-w-2xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">Share File</h2>
        </ModalHeader>

        <ModalBody className="space-y-4">
          {selectedFile && (
            <div>
              <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-50 rounded-lg">
                {getFileIcon(selectedFile.file_type)}
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedFile.file_name}</h3>
                  <p className="text-sm text-gray-600">{formatFileSize(selectedFile.file_size)}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shareable Link (Valid for 7 days)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={shareableLink}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                      onClick={(e) => e.target.select()}
                    />
                    <Button
                      onClick={handleCopyLink}
                      className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                    >
                      {linkCopied ? (
                        <>
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Copied!
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy Link
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">About this link:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>This link will expire in 7 days from now</li>
                        <li>Anyone with this link can download the file</li>
                        <li>No login or authentication required</li>
                        <li>The link is long and secure, but treat it as sensitive</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={() => {
              setIsShareModalOpen(false);
              setShareableLink('');
              setLinkCopied(false);
            }}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Share Folder Modal */}
      <Modal 
        isOpen={isShareFolderModalOpen} 
        onClose={() => {
          setIsShareFolderModalOpen(false);
          setSelectedS3Folder('');
          setFolderFiles([]);
          setCurrentS3Path('');
          setPathHistory([]);
        }}
        className="max-w-3xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">Share S3 Folder to Class</h2>
          <p className="text-sm text-gray-600 mt-1">
            Navigate through folders and select one to share to this class
          </p>
        </ModalHeader>

        <ModalBody className="space-y-4">
          {/* Breadcrumb / Current Path */}
          <div className="flex items-center space-x-2 bg-gray-50 p-3 rounded-md">
            <button
              onClick={handleNavigateBack}
              disabled={pathHistory.length === 0 || loadingFolders}
              className={`p-2 rounded-md transition-colors ${
                pathHistory.length === 0 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
              }`}
              title="Go back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1 flex items-center space-x-1 text-sm">
              <button 
                onClick={async () => {
                  setCurrentS3Path('');
                  setPathHistory([]);
                  setSelectedS3Folder('');
                  await loadFoldersAndFiles('');
                }}
                className="text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Root
              </button>
              {currentS3Path && currentS3Path.split('/').filter(Boolean).map((segment, index, arr) => {
                const pathUpToHere = arr.slice(0, index + 1).join('/') + '/';
                return (
                  <span key={index} className="flex items-center">
                    <svg className="w-4 h-4 text-gray-400 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className={index === arr.length - 1 ? 'text-gray-900 font-medium' : 'text-indigo-600 hover:text-indigo-800 cursor-pointer'}>
                      {segment}
                    </span>
                  </span>
                );
              })}
            </div>
            {currentS3Path && (
              <button
                onClick={() => handleSelectFolder(currentS3Path)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  selectedS3Folder === currentS3Path 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                }`}
              >
                {selectedS3Folder === currentS3Path ? '✓ Selected' : 'Select This Folder'}
              </button>
            )}
          </div>

          {/* Folders List */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Folders {currentS3Path ? `in "${currentS3Path.replace(/\/$/, '').split('/').pop()}"` : '(Root Level)'}
            </label>
            {loadingFolders ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                {s3Folders.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No subfolders in this location
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {s3Folders.map((folder, index) => {
                      const folderName = folder.key;
                      const folderFullPath = folder.fullKey;
                      return (
                        <div
                          key={index}
                          className={`w-full px-4 py-3 flex items-center space-x-3 ${
                            selectedS3Folder === folderFullPath ? 'bg-indigo-50 border-l-4 border-indigo-500' : 'hover:bg-gray-50'
                          }`}
                        >
                          <svg className="w-6 h-6 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                          </svg>
                          <span className="font-medium text-gray-900 flex-1">{folderName}</span>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleSelectFolder(folderFullPath)}
                              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                selectedS3Folder === folderFullPath 
                                  ? 'bg-indigo-600 text-white' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {selectedS3Folder === folderFullPath ? '✓ Selected' : 'Select'}
                            </button>
                            <button
                              onClick={() => handleNavigateIntoFolder(folderFullPath)}
                              className="p-1.5 text-indigo-600 hover:bg-indigo-100 rounded-md transition-colors"
                              title="Open folder"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Files in current folder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Files in Folder ({folderFiles.length})
            </label>
            {loadingFolders ? (
              <div className="flex items-center justify-center py-8 border border-gray-300 rounded-md">
                <div className="animate-spin w-6 h-6 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                {folderFiles.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No files in this folder
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {folderFiles.map((file, index) => (
                      <div key={index} className="px-4 py-2 flex items-center space-x-3">
                        <span className="text-lg">{getFileIcon(file.key.split('.').pop())}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{file.key}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedS3Folder && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-1">Selected folder:</p>
                  <p className="text-green-700">{selectedS3Folder}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How to use:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Click the arrow button to navigate into a folder</li>
                  <li>Click "Select" or "Select This Folder" to choose a folder to share</li>
                  <li>Use the back arrow or breadcrumb to navigate up</li>
                  <li>The folder and all its contents will be shared to the class</li>
                </ul>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={() => {
              setIsShareFolderModalOpen(false);
              setSelectedS3Folder('');
              setFolderFiles([]);
              setCurrentS3Path('');
              setPathHistory([]);
            }}
            className="mr-2 bg-gray-500 hover:bg-gray-600"
            disabled={sharingFolder}
          >
            Cancel
          </Button>
          <Button
            onClick={handleShareFolderToClass}
            disabled={!selectedS3Folder || sharingFolder}
            className="bg-green-600 hover:bg-green-700"
          >
            {sharingFolder ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block"></div>
                Sharing...
              </>
            ) : (
              'Share Folder to Class'
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ClassSharedFiles;
